<template>
  <div id="user-password">
    <div class="content">
      <h3>{{ $t('CHANGE_PASSWORD') }}</h3>

      <form @submit="changePassword" v-if="!willLogout">
        <b-container>
          <b-row>
            <b-col xl="2"><p class="subcont">{{ $t('CURRENT_PASSWORD') }}</p></b-col>
            <b-col xl="3">
              <text-input
                type="password"
                v-model="currentPassword"
                required
                ref="currentPasswordInput"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col xl="2"><p class="subcont">{{ $t('NEW_PASSWORD') }}:</p></b-col>
            <b-col xl="3">
              <text-input
                type="password"
                v-model="newPassword"
                required
                ref="newPasswordInput"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col xl="2"><p class="subcont">{{ $t('CONFIRM_PASSWORD') }}:</p></b-col>
            <b-col xl="3">
              <text-input
                type="password"
                v-model="confirmPassword"
                required
                ref="confirmPasswordInput"
              />
            </b-col>
          </b-row>
          <b-col lg="5" class="btn" v-if="isDesktop">
            <primary-button :text="$t('LAST_MODIFY')" blue @click="changePassword" type="submit" />
            <primary-button :text="$t('BACK')" grey @click="back"/>
          </b-col>
          <b-col v-else class="btn-mobile">
            <primary-button :text="$t('LAST_MODIFY')" blue @click="changePassword" type="submit" />
            <primary-button :text="$t('BACK')" grey @click="back"/>
          </b-col>
        </b-container>
      </form>
      <div v-if="willLogout" class="timer">
        <p>{{ $t('UPDATED_PASSWORD_SUCCESS') }}<span><strong> {{ timeLeft }}</strong></span> s</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import { ERROR_CODES, PASSWORD_MIN_LENGTH } from '@/consts';

import TextInput from '@/components/TextInput';
import PrimaryButton from '@/components/PrimaryButton';
import userService from '@/services/user';

const { useActions: useFlashActions } = createNamespacedHelpers('flash');
const { useActions: useUserActions } = createNamespacedHelpers('user');
const { useState: useAppState } = createNamespacedHelpers('app');

export default {
  name: 'UserPassword',
  components: {
    TextInput,
    PrimaryButton
  },
  metaInfo() {
    const { $i18n } = this;
    const title = $i18n.t('CHANGE_PASSWORD');

    return {
      title,
      meta: []
    };
  },
  setup(_, { root }) {
    const { error } = useFlashActions(['error']);
    const { logout } = useUserActions(['logout']);
    const { isDesktop } = useAppState(['isDesktop']);

    const currentPassword = ref('');
    const currentPasswordInput = ref(null);
    const newPassword = ref('');
    const newPasswordInput = ref(null);
    const confirmPassword = ref('');
    const confirmPasswordInput = ref(null);

    const willLogout = ref(false);
    const timeLeft = ref(5);

    const reset = async () => {
      currentPassword.value = '';
      newPassword.value = '';
      confirmPassword.value = '';

      const inputs = [currentPasswordInput.value, newPasswordInput.value, confirmPasswordInput.value];
      inputs.forEach((input) => {
        input.clearValidateMessage();
      });
    };

    const countDownTimer = () => {
      if (timeLeft.value > 0) {
        setTimeout(() => {
          timeLeft.value -= 1;
          countDownTimer();
        }, 1000);
      }
      if (timeLeft.value <= 0) {
        logout();
      }
    };

    const changePassword = async (e) => {
      if (e) e.preventDefault();

      const inputs = [currentPasswordInput.value, newPasswordInput.value, confirmPasswordInput.value];

      let valid = true;

      inputs.forEach((input) => {
        input.validateInput();
        valid = valid && input.isValid;
      });

      if (valid) {
        const payload = {
          currentPassword: currentPassword.value,
          newPassword: newPassword.value,
          confirmPassword: confirmPassword.value
        };
        try {
          await userService.updatePassword(payload);
          willLogout.value = true;
          countDownTimer();
        } catch(err) {
          const { code } = err;
          const changePasswordErrorCodes = [
            ERROR_CODES.AUTH_INCORRECT_PASSWORD,
            ERROR_CODES.AUTH_CONFIRM_PASSWORD_NOT_MATCH,
            ERROR_CODES.AUTH_NEW_PASSWORD_IS_THE_SAME_CURRENT_PASSWORD,
          ];
          const i = changePasswordErrorCodes.findIndex((c) => c === code);

          if (i === 0) {
            error('INCORRECT_PASSWORD');
          } else if (i === 1) {
            error('CONFIRM_PASSWORD_NOT_MATCH');
          } else if (i === 2) {
            error('NEW_PASSWORD_IS_SAME_WITH_CURRENT_PASSWORD');
          } else {
            error('CHANGE_PASSWORD_ERROR');
          }
          reset();
        }
      }
    };

    const back = () => root.$router.push({ name: 'orders' });

    watch(confirmPassword, () => {
      if (confirmPassword.value !== newPassword.value && confirmPassword.value.length >= PASSWORD_MIN_LENGTH) {
        confirmPasswordInput.value.alert(root.$t('CONFIRM_PASSWORD_NOT_MATCH'));
      }
    });

    onMounted(() => {
      reset();
    });

    return {
      currentPassword,
      currentPasswordInput,
      newPassword,
      newPasswordInput,
      confirmPassword,
      confirmPasswordInput,
      changePassword,
      back,
      willLogout,
      timeLeft,
      isDesktop
    };
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/colors";

#user-password {
  padding: 30px;

  .container {
    margin-left: 0px;
    padding-left: 0px;

    .row {
      margin-bottom: 20px;

      &:first-child {
        margin-top: 20px;
      }

      .text-input {
        .input {
          input {
            border: 1px solid $spanishGrey;
            border-radius: 5px;
          }
        }
      }

    }

    .btn {
      display: flex;
      justify-content: flex-end;

      .primary-button {
        &:first-child {
          margin-right: 20px;
        }

        &:last-child {
          margin-right: 5px;
        }
      }
    }
  }

  .timer {
    border-left: 5px solid $alto;
    padding: 10px 5px 5px;

    p {
      padding-bottom: 0px;
    }
  }

  @media (max-width: 992px) {
    .col {
      padding: 0px;
    }
    .btn-mobile {
      display: block;

      .primary-button {
        width: 100%;
        border-radius: 3px;

        &:first-child {
          margin-bottom: 10px;
        }
      }
    }
  }
}
</style>
